import React, { useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { Marker, GeoJSON, useMap, Popup } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import AppContext, { isRouteTrack, isTrack, OBJECT_TYPE_NAVIGATION_ALONE } from '../../context/AppContext';
import MapContext from '../../context/MapContext';
import MarkerOptions from '../markers/MarkerOptions';
import { getStartPointIconSvg } from '../markers/StartPointMarker';
import { getIntermediatePointIcon, getIntermediatePointIconHtml } from '../markers/IntermediatePointMarker';
import { getDestinationPointIconSvg } from '../markers/DestinationPointMarker';
import { ICON_STATE_DEFAULT, ICON_STATE_HOVER, ICON_STATE_MOVED } from '../markers/trackPointMarkerFactory';
import { fitBoundsOptions } from '../../manager/track/TracksManager';
import {
    ROUTE_POINTS_START,
    ROUTE_POINTS_FINISH,
    ROUTE_POINTS_VIA,
    ROUTE_POINTS_AVOID_ROADS,
} from '../../store/geoRouter/profileConstants';
import { NAVIGATE_URL } from '../../manager/GlobalManager';
import { navigationObject } from '../../store/navigationObject/navigationObject';
import { pickNextRoutePoint } from '../../manager/NavigationManager';
import { POINT_MARKER_Z_INDEX_OFFSET } from '../util/ZIndexes';
import {
    alternativeRouteStyle,
    ALTERNATIVE_ROUTE_OPACITY,
    isAlternativeFeature,
} from '../../store/geoRouter/legacy/calculateRoute';
import { LINE_STRING } from '../../util/Utils';

const DRAG_DEBOUNCE_MS = 10;
const ALTERNATIVE_HOVER_OPACITY = 0.9;
const TURN_DOT_Z_INDEX_OFFSET = 1100;

function setMarkerIconHtml(marker, html) {
    const el = marker?.getElement();
    if (el && html) el.innerHTML = html;
}

function getMarkerIndex(marker) {
    return marker?.options?.['data-index'];
}

function moveableMarker(routeObject, map, marker) {
    let startPx = null;
    let startLL = null; // LatLng

    function trackCursor(evt) {
        marker.setLatLng(evt.latlng);
    }

    marker.on('mousedown', () => {
        startLL = marker.getLatLng();
        startPx = map.latLngToLayerPoint(startLL);
        map.dragging.disable();
        map.on('mousemove', trackCursor);
    });

    marker.on('mouseup', () => {
        map.dragging.enable();
        map.off('mousemove', trackCursor);

        if (!startPx) return;
        const endPx = map.latLngToLayerPoint(marker.getLatLng());
        const moved = Math.abs(endPx.x - startPx.x) + Math.abs(endPx.y - startPx.y);

        if (moved > 10) {
            routeObject.routeAddViaPoint({ ll: marker.getLatLng(), old: startLL });
        }

        startPx = null;
        startLL = null;
    });

    return marker;
}

const NavigationLayer = ({ geocodingData, region }) => {
    const map = useMap();
    const ctx = useContext(AppContext);
    const { t } = useTranslation();
    const mtx = useContext(MapContext);

    const makeDotIcon = useCallback((color = '#ff7800', opacity = 0.9, size = 16, border = '#000', strokeWidth = 1) => {
        const r = size / 2 - strokeWidth / 2;
        const c = size / 2;
        const svg =
            `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block">` +
            `<circle cx="${c}" cy="${c}" r="${r}" fill="${color}" fill-opacity="${opacity}" stroke="${border}" stroke-width="${strokeWidth}"/>` +
            `</svg>`;
        return L.divIcon({
            className: 'nav-dot-icon',
            html: svg,
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
        });
    }, []);

    const routeObject = ctx.routeObject;

    const routeObjectRef = useRef(routeObject);
    const viaInputsCountRef = useRef(ctx.viaInputsCount);

    useEffect(() => {
        routeObjectRef.current = routeObject;
        viaInputsCountRef.current = ctx.viaInputsCount;
    }, [routeObject, ctx.viaInputsCount]);

    const updateCursor = useCallback(() => {
        const container = map.getContainer();
        if (!globalThis.location.pathname.includes(NAVIGATE_URL)) {
            container.style.cursor = '';
            return;
        }
        container.style.cursor = pickNextRoutePoint(routeObjectRef.current, viaInputsCountRef.current || 0)
            ? 'crosshair'
            : '';
    }, []);

    const handleMapClick = useCallback(
        (event) => {
            if (!globalThis.location.pathname.includes(NAVIGATE_URL)) {
                return;
            }
            if (event?.originalEvent?.button !== 0) {
                return;
            }
            const coords = event?.latlng || event.detail?.latlng;
            if (!coords) {
                return;
            }
            const wpt = event.detail?.wpt;

            // Find first empty input from top to bottom (start -> intermediates -> finish)
            const target = pickNextRoutePoint(routeObjectRef.current, viaInputsCountRef.current || 0);
            if (!target) {
                updateCursor();
                return;
            }

            // Signal that we're handling this event (found empty input)
            if (event.preventDefault) {
                event.preventDefault();
            }

            const point = wpt ? navigationObject.fromWpt(wpt, wpt.type) : L.latLng(coords.lat, coords.lng);

            // remove focus from all inputs
            globalThis.dispatchEvent(new Event('nav-blur'));

            // Handle intermediate point
            if (target.type === ROUTE_POINTS_VIA) {
                const viaPoints = routeObjectRef.current.getOption(ROUTE_POINTS_VIA) || [];
                const newViaPoints = [...viaPoints];
                while (newViaPoints.length <= target.index) {
                    newViaPoints.push(null);
                }
                newViaPoints[target.index] = point;
                routeObjectRef.current.setOption(ROUTE_POINTS_VIA, newViaPoints);
            } else {
                // Handle start or finish point
                routeObjectRef.current.setOption(target, point);
            }

            ctx.setRouteTrackFile(null);

            updateCursor();
            if (event?.originalEvent) {
                event.originalEvent.navigationHandled = true;
            }
        },
        [ctx.setRouteTrackFile]
    );

    useEffect(() => {
        const container = map.getContainer();

        // Don't register click handler when context menu is open
        if (ctx.openContextMenu) {
            updateCursor();
            map.on('mousemove', updateCursor);
            return () => {
                map.off('mousemove', updateCursor);
                container.style.cursor = '';
            };
        }

        updateCursor();
        map.on('click', handleMapClick);
        map.on('mousemove', updateCursor);
        globalThis.addEventListener('nav-marker-click', handleMapClick);

        return () => {
            map.off('click', handleMapClick);
            map.off('mousemove', updateCursor);
            globalThis.removeEventListener('nav-marker-click', handleMapClick);
            container.style.cursor = '';
        };
    }, [ctx.openContextMenu]);

    const startPoint = routeObject.getOption(ROUTE_POINTS_START);
    const finishPoint = routeObject.getOption(ROUTE_POINTS_FINISH);
    const viaPoints = routeObject.getOption(ROUTE_POINTS_VIA);
    const avoidRoads = routeObject.getOption(ROUTE_POINTS_AVOID_ROADS);

    const viaPointIcons = useMemo(() => {
        const count = viaPoints?.length ?? 0;
        const icons = [];
        for (let i = 0; i < count; i++) {
            icons.push(getIntermediatePointIcon(i));
        }
        return icons;
    }, [viaPoints?.length]);

    let timer = null;
    function debouncer(f) {
        if (timer === null) {
            timer = setTimeout(() => {
                timer = null;
                f();
            }, DRAG_DEBOUNCE_MS);
        }
    }

    const startPointRef = useRef(null);
    const finishPointRef = useRef(null);
    const pinPointRef = useRef(null);
    const startEventHandlers = useCallback(
        {
            drag(e) {
                const marker = e.target;
                setMarkerIconHtml(marker, getStartPointIconSvg(ICON_STATE_MOVED));
                if (marker != null) debouncer(() => routeObject.setOption(ROUTE_POINTS_START, marker.getLatLng()));
            },
            dragstart(e) {
                routeObject.onDragStart();
                setMarkerIconHtml(e.target, getStartPointIconSvg(ICON_STATE_MOVED));
            },
            dragend(e) {
                routeObject.onDragEnd();
                const marker = e.target;
                if (marker != null) {
                    setMarkerIconHtml(marker, getStartPointIconSvg(ICON_STATE_DEFAULT));
                    routeObject.setOption(ROUTE_POINTS_START, marker.getLatLng());
                    ctx.setRouteTrackFile(null);
                }
            },
            mouseover(e) {
                setMarkerIconHtml(e.target, getStartPointIconSvg(ICON_STATE_HOVER));
            },
            mouseout(e) {
                setMarkerIconHtml(e.target, getStartPointIconSvg(ICON_STATE_DEFAULT));
            },
            click() {
                if (!globalThis.location.pathname.includes(NAVIGATE_URL)) {
                    ctx.setCurrentObjectType(OBJECT_TYPE_NAVIGATION_ALONE);
                }
            },
        },
        [startPointRef]
    );

    const endEventHandlers = useCallback(
        {
            drag(e) {
                const marker = e.target;
                setMarkerIconHtml(marker, getDestinationPointIconSvg(ICON_STATE_MOVED));
                if (marker != null) debouncer(() => routeObject.setOption(ROUTE_POINTS_FINISH, marker.getLatLng()));
            },
            dragstart(e) {
                routeObject.onDragStart();
                setMarkerIconHtml(e.target, getDestinationPointIconSvg(ICON_STATE_MOVED));
            },
            dragend(e) {
                routeObject.onDragEnd();
                const marker = e.target;
                if (marker != null) {
                    setMarkerIconHtml(marker, getDestinationPointIconSvg(ICON_STATE_DEFAULT));
                    routeObject.setOption(ROUTE_POINTS_FINISH, marker.getLatLng());
                    ctx.setRouteTrackFile(null);
                }
            },
            mouseover(e) {
                setMarkerIconHtml(e.target, getDestinationPointIconSvg(ICON_STATE_HOVER));
            },
            mouseout(e) {
                setMarkerIconHtml(e.target, getDestinationPointIconSvg(ICON_STATE_DEFAULT));
            },
            click() {
                if (!globalThis.location.pathname.includes(NAVIGATE_URL)) {
                    ctx.setCurrentObjectType(OBJECT_TYPE_NAVIGATION_ALONE);
                }
            },
        },
        [finishPointRef]
    );

    const pinEventHandlers = useCallback(
        {
            dragend() {
                const marker = pinPointRef.current;
                if (marker != null) {
                    mtx.setPinPoint(marker.getLatLng());
                    ctx.setRouteTrackFile(null);
                }
            },
        },
        [pinPointRef]
    );

    const intermediateEventHandlers = useCallback(
        {
            drag(event) {
                const marker = event.target;
                const ind = getMarkerIndex(marker);
                setMarkerIconHtml(marker, getIntermediatePointIconHtml(ICON_STATE_MOVED, ind));
                debouncer(() => {
                    if (ind == null) return;
                    const newViaPoints = Object.assign([], viaPoints);
                    newViaPoints[ind] = marker.getLatLng();
                    routeObject.setOption(ROUTE_POINTS_VIA, newViaPoints);
                });
            },
            dragstart(event) {
                routeObject.onDragStart();
                setMarkerIconHtml(
                    event.target,
                    getIntermediatePointIconHtml(ICON_STATE_MOVED, getMarkerIndex(event.target))
                );
            },
            dragend(event) {
                routeObject.onDragEnd();
                const marker = event.target;
                if (marker != null) {
                    const ind = getMarkerIndex(marker);
                    setMarkerIconHtml(marker, getIntermediatePointIconHtml(ICON_STATE_DEFAULT, ind));
                    const newViaPoints = Object.assign([], viaPoints);
                    newViaPoints[ind] = marker.getLatLng();
                    routeObject.setOption(ROUTE_POINTS_VIA, newViaPoints);
                }
            },
            mouseover(event) {
                setMarkerIconHtml(
                    event.target,
                    getIntermediatePointIconHtml(ICON_STATE_HOVER, getMarkerIndex(event.target))
                );
            },
            mouseout(event) {
                setMarkerIconHtml(
                    event.target,
                    getIntermediatePointIconHtml(ICON_STATE_DEFAULT, getMarkerIndex(event.target))
                );
            },
        },
        [viaPoints]
    );

    const geojsonMarkerOptions = {
        radius: 8,
        fillColor: '#ff7800',
        color: '#000',
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8,
    };

    // "Route 2. 27.5 km (5.1 km shorter), 35 min (7 min longer), 1800 cost (+500)"
    // Everything is compared against the route currently shown on the left.
    const describeAlternative = (props) => {
        const alt = props.overall ?? {};
        const main = routeObject.getRoute()?.features?.[0]?.properties?.overall ?? {};

        const diff = (value, base, unit, digits) => {
            if (!base) return '';
            const d = value - base;
            const shown = digits ? Math.abs(d).toFixed(digits) : Math.round(Math.abs(d));
            if (Number.parseFloat(shown) === 0) return '';
            const key = d > 0 ? 'web:alt_route_longer' : 'web:alt_route_shorter';
            return ` (${t(key, { value: shown, unit })})`;
        };

        const parts = [];
        if (alt.distance) {
            const km = t('km');
            parts.push(
                `${(alt.distance / 1000).toFixed(1)} ${km}` + diff(alt.distance / 1000, main.distance / 1000, km, 1)
            );
        }
        if (alt.time) {
            const min = t('shared_string_minute_lowercase');
            parts.push(`${Math.round(alt.time / 60)} ${min}` + diff(alt.time / 60, main.time / 60, min, 0));
        }
        if (ctx.develFeatures && alt.routingTime) {
            const d = Math.round(alt.routingTime - (main.routingTime ?? 0));
            const delta = main.routingTime ? ` (${d > 0 ? '+' : ''}${d})` : '';
            parts.push(`${Math.round(alt.routingTime)} cost${delta}`);
        }
        return `${t('web:alt_route_label', { n: props.alternative })}. ` + parts.join(', ');
    };

    // the picked route moves to the front and the two routes swap the "alternative" number (lines and turns)
    const selectAlternative = (feature) => {
        const route = routeObject.getRoute();
        const number = feature.properties?.alternative;
        // matched by the number rather than by object identity - the layer may hold a copy
        const index = (route?.features ?? []).findIndex(
            (f) => f.geometry?.type === LINE_STRING && f.properties?.alternative === number
        );
        if (index <= 0) {
            return;
        }
        const color = route.mainRouteStyle?.color ?? routeObject.getColor();
        const features = route.features.map((f) => {
            const properties = { ...f.properties };
            if (f.properties?.alternative === number) {
                delete properties.alternative;
            } else if (!isAlternativeFeature(f)) {
                properties.alternative = number;
            } else {
                return f;
            }
            let style = f.style;
            if (f.geometry?.type === LINE_STRING) {
                style = properties.alternative ? alternativeRouteStyle(color) : (route.mainRouteStyle ?? { color });
            }

            return { ...f, properties, style };
        });
        const picked = features[index];
        features[index] = features[0];
        features[0] = picked;
        routeObject.putRoute({ route: { ...route, features } });
    };

    const onEachAlternative = (feature, layer) => {
        // translucent so the shown route stays readable, but thick enough to click
        layer.bindTooltip(describeAlternative(feature.properties), { sticky: true });
        // the tooltip makes the path focusable and a click would draw the browser focus frame
        layer.on('add', () => {
            const el = layer.getElement();
            el?.removeAttribute('tabindex');
            el?.style.setProperty('outline', 'none');
        });
        layer.on('mouseover', () => layer.setStyle({ opacity: ALTERNATIVE_HOVER_OPACITY }));
        layer.on('mouseout', () => layer.setStyle({ opacity: ALTERNATIVE_ROUTE_OPACITY }));
        layer.on('click', () => selectAlternative(feature));
    };

    const onEachFeature = ({ feature, layer, id = null }) => {
        if (feature.properties?.description) {
            let desc = feature.properties.description;
            if (feature.properties.roadId) {
                const id = feature.properties.roadId;
                const name = 'Way ' + Math.trunc(id / 64);
                const osm = Math.floor(id / 64);

                window['addAvoidRoadId' + id] = () => {
                    const newAvoidRoads = Object.assign([], avoidRoads);
                    newAvoidRoads.push({ id, name });
                    routeObject.setOption(ROUTE_POINTS_AVOID_ROADS, newAvoidRoads);
                };

                desc = `${desc}.
                    <input type="button" value="Avoid" onclick="addAvoidRoadId${id}()"/>
                    <a href="https://openstreetmap.org/way/${osm}" target="_blank">${name}</a>`;
            }
            layer.on('add', function () {
                const el = layer.getElement();
                if (el) {
                    el.setAttribute('id', id);
                }
            });
            layer.bindPopup(desc);
        }
        if (id !== 'se-geojson-search') {
            layer.on('click', () => {
                if (!globalThis.location.pathname.includes(NAVIGATE_URL)) {
                    ctx.setCurrentObjectType(OBJECT_TYPE_NAVIGATION_ALONE);
                }
            });
        }
    };

    // filter features for GeoJSON
    const routeFilter = (feature /*, layer*/) => {
        return !(feature?.geometry?.type === 'Point' && routeObject.getOption('route.map.hidePoints') === true);
    };

    const pointToLayer = (feature, latlng) => {
        let opts = { ...geojsonMarkerOptions };
        if (feature.properties?.description?.includes('[MUTE]')) {
            opts.fillColor = '#777';
        }
        return moveableMarker(
            routeObject,
            map,
            L.marker(latlng, {
                icon: makeDotIcon(opts.fillColor, opts.fillOpacity),
                interactive: true,
                zIndexOffset: TURN_DOT_Z_INDEX_OFFSET,
            })
        );
    };

    const pointToLayerGeoData = (feature, latlng) => {
        let opts = { ...geojsonMarkerOptions };
        if (feature.properties?.index) {
            opts.fillOpacity = Math.min(1 / Math.log(feature.properties.index + 2), 1);
            let clrs = ['#6DD6DA', '#95D9DA', '#A2ABB5', '#AE8CA3', '#817F82'];
            let indx = [2, 5, 7, 10, 20];
            for (var i = 0; i < indx.length; i++) {
                if (feature.properties.index > indx[i]) {
                    opts.fillColor = clrs[i];
                }
            }
        }
        return L.marker(latlng, {
            icon: makeDotIcon(opts.fillColor, opts.fillOpacity),
            interactive: true,
        });
    };

    // GeoJSON requires dynamic key to refresh/refilter
    // used to redraw layer(s) killed after Local Track Editor
    const refreshKey = isRouteTrack(ctx).toString() + isTrack(ctx).toString();
    const routeDataKey = routeObject.getRouteKey() + refreshKey;

    const routeLayerRef = useRef(null);
    const routeLayer = routeLayerRef.current;
    const altLayerRef = useRef(null);

    // own layer mounted before the route: the route stays on top, and zoom-to-route ignores alternatives
    const routeFeatures = routeObject.getRoute()?.features ?? [];
    const shownRoute = routeFeatures.filter((f) => !isAlternativeFeature(f));
    const alternativeRoutes = routeFeatures.filter((f) => isAlternativeFeature(f) && f.geometry?.type === LINE_STRING);

    const viaLayersRef = useRef([]);

    // fitBounds (route)
    // activated on route.map.zoom
    const routeZoom = routeObject.getOption('route.map.zoom');
    useEffect(() => {
        if (routeLayer && routeZoom) {
            routeObject.setOption('route.map.zoom', false);
            map.fitBounds(routeLayer.getBounds(), fitBoundsOptions(mtx));
        }
    }, [routeZoom, routeLayer]);

    // conceal (remove layer)
    // activated on route.map.conceal
    // dep on routeLayer to concel refreshed layer again
    const routeConceal = routeObject.getOption('route.map.conceal');
    useEffect(() => {
        if (routeLayer && routeConceal) {
            // stop conceal only when isRouteTrack mode started
            if (isRouteTrack(ctx)) {
                routeObject.setOption('route.map.conceal', false);
            }

            // avoid conceal if zoom is requested
            if (routeZoom === false) {
                map.removeLayer(routeLayer);
                if (altLayerRef.current) {
                    map.removeLayer(altLayerRef.current);
                }

                // remove start, finish
                map.removeLayer(startPointRef.current);
                map.removeLayer(finishPointRef.current);

                // remove viaPoints (ref array) and reset the array
                viaLayersRef.current.forEach((m) => m && map.removeLayer(m));
                viaLayersRef.current = [];
            }
        }
    }, [routeConceal, routeLayer]);

    // pass geojson.features.style to set colors/etc
    const passStyle = (f) => {
        if (!f.style && f.geometry?.type === LINE_STRING) {
            const color = routeObject.getColor();
            f.style = f.properties?.alternative ? alternativeRouteStyle(color) : { color };
        }
        return f.style;
    };

    return (
        <>
            {/* alternatives make sense on the route map only, not in details or any track editing */}
            {alternativeRoutes.length > 0 && !isTrack(ctx) && (
                <GeoJSON
                    ref={altLayerRef}
                    key={'alt-' + routeDataKey}
                    data={{ type: 'FeatureCollection', features: alternativeRoutes }}
                    style={passStyle}
                    onEachFeature={onEachAlternative}
                />
            )}
            {routeObject.getRoute() && (
                <GeoJSON
                    ref={routeLayerRef}
                    key={routeDataKey}
                    data={{ ...routeObject.getRoute(), features: shownRoute }}
                    style={passStyle}
                    pointToLayer={pointToLayer}
                    onEachFeature={(feature, layer) => onEachFeature({ feature, layer })}
                    filter={routeFilter}
                />
            )}
            {geocodingData && (
                <GeoJSON
                    key={geocodingData.id + refreshKey}
                    data={geocodingData.geojson}
                    pointToLayer={pointToLayerGeoData}
                    onEachFeature={(feature, layer) => onEachFeature({ feature, layer, id: 'se-geojson-search' })}
                />
            )}
            {startPoint && (
                <Marker
                    key={'mark-start' + refreshKey}
                    position={startPoint}
                    icon={MarkerOptions.options.startIcon}
                    ref={startPointRef}
                    draggable={true}
                    eventHandlers={startEventHandlers}
                    zIndexOffset={POINT_MARKER_Z_INDEX_OFFSET}
                />
            )}
            {viaPoints.map((it, ind) =>
                it != null ? (
                    <Marker
                        ref={(m) => m && viaLayersRef.current.push(m)}
                        key={'mark-via' + ind + refreshKey}
                        data-index={ind}
                        position={it}
                        icon={viaPointIcons[ind]}
                        draggable={true}
                        eventHandlers={intermediateEventHandlers}
                        zIndexOffset={POINT_MARKER_Z_INDEX_OFFSET}
                    />
                ) : null
            )}
            {finishPoint && (
                <Marker
                    key={'mark-finish' + refreshKey}
                    position={finishPoint}
                    icon={MarkerOptions.options.endIcon}
                    ref={finishPointRef}
                    draggable={true}
                    eventHandlers={endEventHandlers}
                    zIndexOffset={POINT_MARKER_Z_INDEX_OFFSET}
                />
            )}
            {mtx.pinPoint && (
                <Marker
                    key={'pin-point' + refreshKey}
                    position={mtx.pinPoint}
                    icon={MarkerOptions.options.pointerIcons}
                    ref={pinPointRef}
                    draggable={true}
                    eventHandlers={pinEventHandlers}
                />
            )}
            {region && <Popup position={region.latlng}>{region.regions}</Popup>}
        </>
    );
};

export default NavigationLayer;
